import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield, Scan, Phone, Wifi, Car, Radio, Server, Eye,
  BarChart3, BrainCircuit, Lock, AlertTriangle, Activity,
  Cpu, Globe, Users, Satellite, Fingerprint, Target,
  ArrowRight, ArrowLeft, Play, Pause, ChevronRight
} from 'lucide-react';
import SEO from '../components/SEO';

const demoSteps = [
  {
    id: 'overview',
    title: 'Enterprise Security Dashboard',
    subtitle: 'Complete visibility across your entire security posture',
    icon: Shield,
    color: 'from-orange-500 to-amber-500',
    description: 'The SecureSphere dashboard provides a unified view of your security score, active threats, protected devices, and detection rate. Quick actions let you download agents, view threats, and manage devices in one click.',
    metrics: [
      { label: 'Security Score', value: '92%', color: 'text-green-400' },
      { label: 'Active Threats', value: '3', color: 'text-red-400' },
      { label: 'Protected Devices', value: '52,841', color: 'text-blue-400' },
      { label: 'Detection Rate', value: '99.8%', color: 'text-purple-400' }
    ],
    features: ['Real-time threat monitoring', 'One-click agent deployment', 'Centralized device management']
  },
  {
    id: 'url-scanner',
    title: 'URL & Phishing Scanner',
    subtitle: 'ML-powered real-time malicious URL detection',
    icon: Scan,
    color: 'from-amber-500 to-orange-600',
    description: 'Scan any URL in real-time with our multi-engine threat analysis. Detects phishing, malware distribution, command & control servers, and cryptomining sites with 99.7% accuracy.',
    metrics: [
      { label: 'URLs Scanned', value: '2.4M+', color: 'text-orange-400' },
      { label: 'Threats Blocked', value: '47,293', color: 'text-red-400' },
      { label: 'Avg Scan Time', value: '1.2s', color: 'text-green-400' },
      { label: 'Accuracy', value: '99.7%', color: 'text-blue-400' }
    ],
    features: ['Multi-engine threat analysis', 'Domain reputation scoring', 'Bulk URL scanning', 'API integration ready']
  },
  {
    id: 'sms-analysis',
    title: 'SMS Phishing Analysis',
    subtitle: 'AI-driven smishing attack detection',
    icon: Phone,
    color: 'from-orange-500 to-red-500',
    description: 'Protect your subscribers from SMS phishing (smishing) attacks. Our AI analyzes message content, sender reputation, URL patterns, and behavioral signals to detect fraud in real-time.',
    metrics: [
      { label: 'SMS Analyzed', value: '15M+', color: 'text-orange-400' },
      { label: 'Fraud Blocked', value: '890K', color: 'text-red-400' },
      { label: 'False Positive', value: '0.03%', color: 'text-green-400' },
      { label: 'Languages', value: '12', color: 'text-purple-400' }
    ],
    features: ['Content analysis AI', 'Sender reputation engine', 'URL extraction & scanning', 'Multi-language support']
  },
  {
    id: 'iot-security',
    title: 'IoT Device Security',
    subtitle: 'Comprehensive connected device protection',
    icon: Wifi,
    color: 'from-yellow-500 to-amber-500',
    description: 'Discover, monitor, and protect every IoT device on your network. From smart cameras to industrial sensors, SecureSphere provides firmware scanning, anomaly detection, and automated quarantine.',
    metrics: [
      { label: 'Devices Monitored', value: '89,500', color: 'text-blue-400' },
      { label: 'Firmware Scans', value: '12,400', color: 'text-orange-400' },
      { label: 'Vulnerabilities', value: '342', color: 'text-red-400' },
      { label: 'Categories', value: '8', color: 'text-purple-400' }
    ],
    features: ['Auto-discovery', 'Firmware vulnerability scanning', 'Network segmentation', 'Behavioral anomaly detection']
  },
  {
    id: 'automotive',
    title: 'Automotive Security',
    subtitle: 'V2X, CAN bus, and ECU protection',
    icon: Car,
    color: 'from-amber-400 to-orange-500',
    description: 'Protect connected vehicles with comprehensive automotive security. Monitor CAN bus traffic, detect ECU tampering, secure V2X communications, and ensure ISO 21434 compliance.',
    metrics: [
      { label: 'Vehicles Protected', value: '25,000+', color: 'text-blue-400' },
      { label: 'ECUs Monitored', value: '750K', color: 'text-orange-400' },
      { label: 'CAN Anomalies', value: '1,247', color: 'text-red-400' },
      { label: 'Compliance', value: 'ISO 21434', color: 'text-green-400' }
    ],
    features: ['CAN bus monitoring', 'ECU integrity verification', 'GPS spoofing detection', 'OTA update security']
  },
  {
    id: 'gsm-fraud',
    title: 'GSM Fraud Detection',
    subtitle: 'SIM swap, IMSI catchers, and telecom fraud',
    icon: Radio,
    color: 'from-orange-400 to-red-500',
    description: 'Detect and prevent GSM fraud across your carrier network. Real-time SIM swap detection, IMSI catcher alerts, OTP fraud prevention, and international revenue share fraud blocking.',
    metrics: [
      { label: 'Fraud Attempts', value: '23,847', color: 'text-red-400' },
      { label: 'Revenue Protected', value: '$4.2M', color: 'text-green-400' },
      { label: 'SIM Swaps Blocked', value: '3,421', color: 'text-orange-400' },
      { label: 'IMSI Catchers', value: '12', color: 'text-red-400' }
    ],
    features: ['Real-time SIM swap detection', 'IMSI catcher triangulation', 'OTP fraud prevention', 'Roaming fraud analytics']
  },
  {
    id: 'telecom',
    title: 'Telecom & CSP Operations',
    subtitle: 'SS7, Diameter, and network security',
    icon: Server,
    color: 'from-amber-500 to-orange-500',
    description: 'Monitor and protect carrier signaling networks. SS7, Diameter, and GTP protocol security with real-time threat detection, eSIM lifecycle management, and network slice security for 5G.',
    metrics: [
      { label: 'SS7 Attacks Blocked', value: '5,678', color: 'text-red-400' },
      { label: 'Subscribers', value: '120M+', color: 'text-blue-400' },
      { label: 'Uptime', value: '99.97%', color: 'text-green-400' },
      { label: 'API Latency', value: '12ms', color: 'text-orange-400' }
    ],
    features: ['SS7/Diameter firewall', 'GTP tunnel security', '5G core protection', 'eSIM lifecycle management']
  },
  {
    id: 'ai-security',
    title: 'AI Security Platform',
    subtitle: 'Protect enterprise AI/ML workloads',
    icon: BrainCircuit,
    color: 'from-purple-500 to-orange-500',
    description: 'The industry\'s most comprehensive AI workload security. Monitor LLM API calls, detect prompt injection, enforce agentic AI guardrails, scan model supply chains, and ensure AI compliance across every department.',
    metrics: [
      { label: 'API Calls Monitored', value: '200K/day', color: 'text-blue-400' },
      { label: 'Injections Blocked', value: '1,247', color: 'text-red-400' },
      { label: 'Models Scanned', value: '45', color: 'text-orange-400' },
      { label: 'Compliance Score', value: '92%', color: 'text-green-400' }
    ],
    features: ['Prompt injection detection', 'PII auto-redaction', 'Agentic AI guardrails', 'AI SBOM scanning', 'EU AI Act compliance', 'Department cost tracking']
  },
  {
    id: 'analytics',
    title: 'Analytics & Intelligence',
    subtitle: 'Executive dashboards and threat intelligence',
    icon: BarChart3,
    color: 'from-yellow-500 to-orange-500',
    description: 'Comprehensive analytics with threat trend analysis, attack vector mapping, geographic threat visualization, and compliance reporting. Includes competitor gap analysis against Aeris WatchTower and PaloAlto.',
    metrics: [
      { label: 'Threat Trends', value: '90-day view', color: 'text-orange-400' },
      { label: 'Attack Vectors', value: '24 tracked', color: 'text-red-400' },
      { label: 'Compliance Score', value: '94%', color: 'text-green-400' },
      { label: 'Reports/Month', value: '12', color: 'text-blue-400' }
    ],
    features: ['Executive dashboards', 'Threat trend analysis', 'Geographic visualization', 'Compliance reporting', 'Zero-day detection', 'Predictive threat hunting']
  },
  {
    id: 'realtime',
    title: 'Real-Time Threat Alerts',
    subtitle: 'Live WebSocket notifications across all channels',
    icon: Activity,
    color: 'from-red-500 to-orange-500',
    description: 'Never miss a critical event. Real-time threat alerts via WebSocket push, with multi-channel delivery through push notifications, email, SMS, Slack, and webhook integrations. Configurable routing rules and escalation policies.',
    metrics: [
      { label: 'Channels', value: '6', color: 'text-orange-400' },
      { label: 'Avg Delivery', value: '<2s', color: 'text-green-400' },
      { label: 'Alerts/Day', value: '55+', color: 'text-red-400' },
      { label: 'Escalation', value: 'Auto', color: 'text-blue-400' }
    ],
    features: ['WebSocket live push', 'Multi-channel delivery', 'Severity-based routing', 'Auto-escalation policies', 'Notification bell in dashboard']
  }
];

const DemoPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const timerRef = useRef(null);

  const advanceStep = useCallback(() => {
    setCurrentStep(prev => (prev + 1) % demoSteps.length);
  }, []);

  useEffect(() => {
    if (isPlaying) {
      timerRef.current = setInterval(advanceStep, 8000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [isPlaying, advanceStep]);

  const goTo = (idx) => {
    setCurrentStep(idx);
    setIsPlaying(false);
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const step = demoSteps[currentStep];
  const StepIcon = step.icon;
  const progress = ((currentStep + 1) / demoSteps.length) * 100;

  return (
    <div className="min-h-screen bg-[#1a1a1a] text-white">
      <SEO title="Platform Demo | SecureSphere by Infuse.AI" description="Interactive walkthrough of SecureSphere's enterprise security platform. Explore URL scanning, IoT security, automotive protection, AI security, and more." />

      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#1a1a1a]/95 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-3 hover:opacity-80 transition">
            <img src="/infuse-logo.jpg" alt="Infuse.AI" className="h-10 w-10 rounded-lg" />
            <span className="text-xl font-bold"><span className="text-white">Infuse</span><span className="text-[#F26419]">.AI</span><span className="text-neutral-400 text-xs align-top">™</span></span>
          </button>
          <div className="flex items-center gap-4">
            <span className="text-sm text-neutral-400 hidden md:block">{currentStep + 1} / {demoSteps.length}</span>
            <button onClick={() => navigate('/login')} className="px-5 py-2.5 bg-[#F26419] text-white font-semibold rounded-lg text-sm hover:bg-[#F7B731] hover:text-black transition-all">Start Free Trial</button>
          </div>
        </div>
      </nav>

      {/* Progress Bar */}
      <div className="fixed top-[73px] left-0 right-0 z-40 h-1 bg-black/30">
        <motion.div className="h-full bg-gradient-to-r from-[#F7B731] to-[#F26419]" animate={{ width: `${progress}%` }} transition={{ duration: 0.5 }} />
      </div>

      <div className="pt-24 pb-8 max-w-7xl mx-auto px-6">
        {/* Step Navigation Dots */}
        <div className="flex items-center justify-center gap-2 mb-8 flex-wrap">
          {demoSteps.map((s, i) => {
            const Icon = s.icon;
            return (
              <button key={s.id} onClick={() => goTo(i)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-all ${i === currentStep ? 'bg-orange-500/20 text-orange-400 border border-orange-500/40' : 'text-neutral-500 hover:text-white hover:bg-white/5'}`}>
                <Icon className="w-3.5 h-3.5" />
                <span className="hidden lg:inline">{s.title.split(' ')[0]}</span>
              </button>
            );
          })}
        </div>

        {/* Main Content */}
        <AnimatePresence mode="wait">
          <motion.div key={step.id} initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }} transition={{ duration: 0.4 }}>
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              {/* Left: Info */}
              <div className="space-y-8">
                <div>
                  <div className={`inline-flex items-center gap-2 w-14 h-14 rounded-2xl bg-gradient-to-br ${step.color} justify-center mb-5`}>
                    <StepIcon className="w-7 h-7 text-black" />
                  </div>
                  <h2 className="text-3xl font-bold text-white mb-2">{step.title}</h2>
                  <p className="text-orange-400 font-medium">{step.subtitle}</p>
                </div>
                <p className="text-neutral-300 leading-relaxed text-lg">{step.description}</p>
                <div>
                  <h4 className="text-sm text-neutral-500 uppercase tracking-wider mb-3">Key Features</h4>
                  <div className="space-y-2">
                    {step.features.map((f, i) => (
                      <motion.div key={f} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.1 }} className="flex items-center gap-2">
                        <ChevronRight className="w-4 h-4 text-[#F7B731]" />
                        <span className="text-neutral-300">{f}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right: Metrics Dashboard */}
              <div className="space-y-6">
                <div className={`bg-[#242424] border border-white/10 rounded-2xl p-8 relative overflow-hidden`}>
                  <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${step.color}`} />
                  <h3 className="text-lg font-semibold text-white mb-6">Live Metrics</h3>
                  <div className="grid grid-cols-2 gap-6">
                    {step.metrics.map((m, i) => (
                      <motion.div key={m.model || m.name || i} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.1 + i * 0.1 }} className="bg-[#1a1a1a] rounded-xl p-5">
                        <p className="text-xs text-neutral-500 mb-1">{m.label}</p>
                        <p className={`text-2xl font-bold ${m.color}`}>{m.value}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Simulated Activity */}
                <div className="bg-[#242424] border border-white/10 rounded-2xl p-6">
                  <h3 className="text-sm text-neutral-500 uppercase tracking-wider mb-4">Recent Activity</h3>
                  <div className="space-y-3">
                    {[
                      { color: 'bg-red-500', msg: `${step.title} - Critical event detected`, time: '2s ago' },
                      { color: 'bg-orange-500', msg: `${step.title} - Policy enforcement triggered`, time: '15s ago' },
                      { color: 'bg-green-500', msg: `${step.title} - Threat successfully blocked`, time: '1m ago' },
                    ].map((item) => (
                      <motion.div key={item.msg} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-3 p-3 bg-[#1a1a1a] rounded-lg">
                        <span className={`w-2 h-2 rounded-full ${item.color} animate-pulse`} />
                        <span className="text-sm text-neutral-300 flex-1">{item.msg}</span>
                        <span className="text-xs text-neutral-500">{item.time}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Controls */}
        <div className="flex items-center justify-between mt-12 pt-8 border-t border-white/10">
          <button onClick={() => { goTo((currentStep - 1 + demoSteps.length) % demoSteps.length); }} className="flex items-center gap-2 text-neutral-400 hover:text-white transition px-4 py-2 rounded-lg hover:bg-white/5">
            <ArrowLeft className="w-5 h-5" /> Previous
          </button>
          <button onClick={() => setIsPlaying(!isPlaying)} className="flex items-center gap-2 px-6 py-3 bg-orange-500/20 text-orange-400 rounded-full hover:bg-orange-500/30 transition">
            {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            {isPlaying ? 'Pause Tour' : 'Resume Tour'}
          </button>
          <button onClick={() => { goTo((currentStep + 1) % demoSteps.length); }} className="flex items-center gap-2 text-neutral-400 hover:text-white transition px-4 py-2 rounded-lg hover:bg-white/5">
            Next <ArrowRight className="w-5 h-5" />
          </button>
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <p className="text-neutral-400 mb-4">Ready to see it in action?</p>
          <div className="flex flex-wrap justify-center gap-4">
            <button onClick={() => navigate('/login')} className="px-8 py-4 bg-gradient-to-r from-[#F7B731] to-[#F26419] text-black font-semibold rounded-full text-lg hover:shadow-[0_0_40px_rgba(242,100,25,0.5)] transition-all flex items-center gap-2">
              Start Free Trial <ArrowRight className="w-5 h-5" />
            </button>
            <a href="mailto:info@infuse.net.in" className="px-8 py-4 bg-white/5 border border-white/20 text-white font-medium rounded-full text-lg hover:bg-white/10 transition-all">Schedule Live Demo</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoPage;
